/**
 * Ref-counted, desired-state subscription manager for the realtime socket.
 *
 * Pure (no React, no socket.io): the transport tells it what happened —
 * connected, disconnected, authenticated as an account, a server `error` or
 * `subscribed` reply — and it decides what goes on the wire through the
 * injected `send`.
 *
 * Why desired state rather than "what we sent":
 *  - Socket.IO reconnects drop every room server-side. The old id map still
 *    listed public channels as subscribed, so nothing re-subscribed them and
 *    the order book, trades, index and tickers stayed dead until a reload
 *    (FE-1). Here `onConnect` replays every public entry.
 *  - Private rooms need an authenticated session for THAT account.
 *    `onAuthenticated(account)` replays the entries for the account and drops
 *    any left over from another wallet.
 *  - Two components on one channel used to cancel each other (FE-4). Entries
 *    are reference counted and only the 0→1 and 1→0 transitions hit the wire.
 *  - Nothing is lost while disconnected: `onDisconnect` only marks entries as
 *    not on the wire (FE-18).
 */

/** Channels scoped to one account; they need an authenticated socket. */
export const PRIVATE_ACCOUNT_CHANNELS: ReadonlySet<string> = new Set([
  "account",
  "portfolio",
  "orders",
  "positions",
  "order-history",
  "trade-history",
  "position-history",
  "notifications",
]);

export const getChannelName = (channelData: string) =>
  String(channelData).split(":")[0];

const isEvmAccount = (value?: string) => /^0x[a-fA-F0-9]{40}$/.test(value || "");

export const isPrivateChannel = (channelData: string) =>
  PRIVATE_ACCOUNT_CHANNELS.has(getChannelName(channelData));

/** Lowercased account a private channel belongs to, or undefined. */
export const getPrivateChannelAccount = (channelData: string) => {
  const [channel, ...rest] = String(channelData).split(":");
  if (!PRIVATE_ACCOUNT_CHANNELS.has(channel)) return undefined;
  if (!rest.length) return undefined;
  const firstAccount = rest.find(isEvmAccount);
  return (firstAccount || rest[0]).toLowerCase();
};

// ── Wire payloads ───────────────────────────────────────────────────────────

/** What the client emits for one channel: `socket.emit(op, { channel, params })`. */
export interface WirePayload {
  channel: string;
  params: Record<string, string | undefined>;
}

/** The `subscribe` payload for an app channel key such as `candles:BTC-PERP:15`. */
export const subscribePayloadFor = (channelData: string): WirePayload => {
  const [channel, ...rest] = String(channelData).split(":");
  const value = rest.join(":") || undefined;

  if (PRIVATE_ACCOUNT_CHANNELS.has(channel)) {
    return { channel, params: { account: value } };
  }
  if (channel === "orderbook" || channel === "trades" || channel === "ticker") {
    return { channel, params: { market: value } };
  }
  if (channel === "candles") {
    // "candles:BTC-PERP:15" — an instrument name never contains a colon, so the
    // second segment is the resolution.
    const [market, resolution] = (value || "").split(":");
    return { channel, params: { market, resolution } };
  }
  return { channel: channelData, params: {} };
};

/** The `unsubscribe` payload: the app channel key as sent. */
export const unsubscribePayloadFor = (channelData: string): WirePayload => ({
  channel: channelData,
  params: {},
});

/**
 * Stable text for a payload as it arrives back from the server: socket.io's
 * JSON encoding drops `undefined`, and key order carries no meaning.
 */
const payloadSignature = (payload: unknown) => {
  const record = payload && typeof payload === "object" ? (payload as any) : {};
  const params = record.params && typeof record.params === "object" ? record.params : {};
  const cleanParams = Object.keys(params)
    .filter((key) => params[key] !== undefined)
    .sort()
    .map((key) => [key, params[key]]);
  return JSON.stringify([record.channel, cleanParams]);
};

/**
 * A channel compared across client and server spellings: case-insensitive
 * (the server uppercases symbols and lowercases addresses), and the
 * notifications room `notifications:{chainId}:{account}` compared without its
 * chain segment, which the client key does not carry.
 */
export const canonicalChannel = (channel: string) => {
  const parts = String(channel).toLowerCase().split(":");
  if (parts[0] === "notifications" && parts.length === 3) {
    return `${parts[0]}:${parts[2]}`;
  }
  return parts.join(":");
};

// ── Server replies ──────────────────────────────────────────────────────────

/** Exact echo of what the client sent, carried by newer servers. */
export interface IWsRequestedEcho {
  channel?: unknown;
  params?: unknown;
}

/** Typed `error` event from the backend. Every field is optional: old servers send only `message`. */
export interface IWsServerError {
  code?: string;
  message?: string;
  /** The canonical room when the server parsed the request, else the channel as sent, or a bare kind. */
  channel?: string;
  /** The bare channel kind (`orderbook`). */
  kind?: string;
  requested?: IWsRequestedEcho;
  retryAfterMs?: number;
  limit?: number;
}

/** `subscribed` / `unsubscribed` reply. */
export interface IWsServerAck {
  channel?: string;
  kind?: string;
  account?: string;
  market?: string;
  resolution?: string;
  requested?: IWsRequestedEcho;
  wasSubscribed?: boolean;
}

export type SubscriptionOp = "subscribe" | "unsubscribe";

export type ServerErrorDisposition =
  /** Never retried for this entry (the request itself is wrong). */
  | "blocked"
  /** Not retried until the next connection. */
  | "stopped"
  | "retry-scheduled"
  /** Re-sent by the next authentication. */
  | "awaiting-auth"
  /** Several entries could be meant; nothing was changed. */
  | "ambiguous"
  | "ignored";

export const RETRY_MIN_DELAY_MS = 1000;
export const RETRY_MAX_DELAY_MS = 30000;
/** RATE_LIMITED retries wait up to this fraction longer than the computed delay. */
export const RATE_LIMIT_JITTER_RATIO = 0.25;
/** SUBSCRIPTION_LIMIT retries per entry before giving up until the next connection. */
export const SUBSCRIPTION_LIMIT_MAX_ATTEMPTS = 5;
/** SNAPSHOT_FAILED / INTERNAL retries per entry before giving up until the next connection. */
export const TRANSIENT_MAX_ATTEMPTS = 8;

/** Errors about the request itself: repeating it can never succeed. */
const NEVER_RETRY_CODES = new Set([
  "UNKNOWN_CHANNEL",
  "INVALID_CHANNEL",
  "INVALID_PARAMS",
  "INVALID_RESOLUTION",
]);

/**
 * Delay before retrying after a limit error: exponential in the attempt
 * number, never shorter than the server's `retryAfterMs`, always within
 * [RETRY_MIN_DELAY_MS, RETRY_MAX_DELAY_MS]. A missing or garbage
 * `retryAfterMs` can therefore never produce an instant retry storm (FE-19).
 */
export const clampedBackoffMs = (attempt: number, retryAfterMs?: unknown) => {
  const exponential = RETRY_MIN_DELAY_MS * 2 ** Math.max(0, attempt);
  const serverHint =
    typeof retryAfterMs === "number" && Number.isFinite(retryAfterMs)
      ? retryAfterMs
      : 0;
  return Math.min(
    RETRY_MAX_DELAY_MS,
    Math.max(RETRY_MIN_DELAY_MS, exponential, serverHint)
  );
};

export interface SubscriptionManagerOptions {
  /** Puts one channel on the wire. Only called while the manager believes the socket is connected. */
  send: (op: SubscriptionOp, channel: string) => void;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  /** [0, 1); jitter source for RATE_LIMITED retries. */
  random?: () => number;
  /** Telemetry for replies the manager could not act on (e.g. a Sentry breadcrumb). */
  report?: (message: string, data?: Record<string, unknown>) => void;
}

export interface SubscriptionEntryState {
  channel: string;
  refs: number;
  isPrivate: boolean;
  account?: string;
  onWire: boolean;
  /** The server confirmed the current subscribe with `subscribed`. */
  acked: boolean;
  blocked: boolean;
  /** Given up until the next connection. */
  stopped: boolean;
  retryPending: boolean;
  attempts: number;
}

interface Entry {
  channel: string;
  refs: number;
  isPrivate: boolean;
  account?: string;
  onWire: boolean;
  acked: boolean;
  blocked: boolean;
  stopped: boolean;
  attempts: number;
  /** The current subscribe drew a SNAPSHOT_FAILED / INTERNAL, so its ack must not reset `attempts`. */
  failedThisCycle: boolean;
  retryTimer?: unknown;
}

export interface SubscriptionManager {
  acquire: (channel: string) => void;
  release: (channel: string) => void;
  onConnect: () => void;
  onDisconnect: () => void;
  onAuthenticated: (account: string) => void;
  onDeauthenticated: () => void;
  handleServerError: (error: IWsServerError | undefined) => ServerErrorDisposition;
  /** A `subscribed` reply; true when it matched an entry. */
  handleServerAck: (ack: IWsServerAck | undefined) => boolean;
  /**
   * Register a REST refresh for a channel, run when its snapshot failed or its
   * frames were dropped. Returns the unregister function.
   */
  registerRevalidate: (channel: string, revalidate: () => void) => () => void;
  /** Run the REST refreshes registered for a channel, in client or server spelling. */
  revalidate: (serverChannel: string) => boolean;
  isConnected: () => boolean;
  getAuthenticatedAccount: () => string | undefined;
  getEntry: (channel: string) => SubscriptionEntryState | undefined;
  getEntries: () => SubscriptionEntryState[];
  dispose: () => void;
}

export const createSubscriptionManager = ({
  send,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
  random = Math.random,
  report = () => {},
}: SubscriptionManagerOptions): SubscriptionManager => {
  const entries = new Map<string, Entry>();
  const revalidators = new Map<string, Set<() => void>>();
  let connected = false;
  let authenticatedAccount: string | undefined;

  const canWire = (entry: Entry) =>
    connected &&
    !entry.blocked &&
    !entry.stopped &&
    (!entry.isPrivate ||
      (authenticatedAccount !== undefined &&
        entry.account === authenticatedAccount));

  const cancelRetry = (entry: Entry) => {
    if (entry.retryTimer !== undefined) {
      clearTimer(entry.retryTimer);
      entry.retryTimer = undefined;
    }
  };

  const sendSubscribe = (entry: Entry) => {
    entry.onWire = true;
    entry.acked = false;
    entry.failedThisCycle = false;
    send("subscribe", entry.channel);
  };

  const subscribe = (entry: Entry) => {
    if (entry.onWire || entry.retryTimer !== undefined || !canWire(entry)) {
      return;
    }
    sendSubscribe(entry);
  };

  const unsubscribe = (entry: Entry) => {
    if (entry.onWire && connected) {
      send("unsubscribe", entry.channel);
    }
    entry.onWire = false;
    entry.acked = false;
  };

  /** Resets the per-connection verdicts; `blocked` is about the request and survives. */
  const resetForSession = (entry: Entry) => {
    entry.onWire = false;
    entry.acked = false;
    entry.stopped = false;
    entry.attempts = 0;
    entry.failedThisCycle = false;
    cancelRetry(entry);
  };

  const byCanonical = (serverChannel: string) => {
    const exact = entries.get(serverChannel);
    if (exact) return exact;
    const wanted = canonicalChannel(serverChannel);
    return Array.from(entries.values()).find(
      (entry) => canonicalChannel(entry.channel) === wanted
    );
  };

  /**
   * The entry a server reply is about.
   *
   * 1. `requested` — the exact payload we sent — is authoritative when present;
   * 2. else the channel, canonicalised on both sides;
   * 3. else, when only a kind is known, the single entry of that kind still
   *    awaiting its reply. Two or more such entries are ambiguous: nothing is
   *    touched, and the case is reported.
   */
  const match = (
    reply: { requested?: IWsRequestedEcho; channel?: unknown; kind?: unknown },
    channelFromFields?: string
  ): Entry | "ambiguous" | undefined => {
    const { requested } = reply;
    if (requested && typeof requested === "object" && typeof requested.channel === "string") {
      const wanted = payloadSignature(requested);
      return Array.from(entries.values()).find(
        (entry) => payloadSignature(subscribePayloadFor(entry.channel)) === wanted
      );
    }

    const channel = channelFromFields ?? (typeof reply.channel === "string" ? reply.channel : undefined);
    if (channel && channel.includes(":")) return byCanonical(channel);

    const kind = (typeof reply.kind === "string" ? reply.kind : channel)?.toLowerCase();
    if (!kind) return undefined;
    const candidates = Array.from(entries.values()).filter(
      (entry) =>
        getChannelName(entry.channel).toLowerCase() === kind &&
        entry.onWire &&
        !entry.acked
    );
    if (candidates.length === 1) return candidates[0];
    if (candidates.length > 1) {
      report("ambiguous server reply", { kind, candidates: candidates.length });
      return "ambiguous";
    }
    return undefined;
  };

  const runRevalidators = (channel: string) => {
    const fns = revalidators.get(channel);
    if (!fns?.size) return false;
    fns.forEach((fn) => {
      try {
        fn();
      } catch (error) {
        report("revalidate failed", { channel, error: String(error) });
      }
    });
    return true;
  };

  const scheduleRetry = (entry: Entry, delay: number, resend: boolean) => {
    cancelRetry(entry);
    entry.retryTimer = setTimer(() => {
      entry.retryTimer = undefined;
      if (entries.get(entry.channel) !== entry) return;
      if (resend && entry.onWire) {
        // The server kept the room; ask again for the snapshot it could not load.
        if (canWire(entry)) sendSubscribe(entry);
        return;
      }
      subscribe(entry);
    }, delay);
  };

  const toState = (entry: Entry): SubscriptionEntryState => ({
    channel: entry.channel,
    refs: entry.refs,
    isPrivate: entry.isPrivate,
    account: entry.account,
    onWire: entry.onWire,
    acked: entry.acked,
    blocked: entry.blocked,
    stopped: entry.stopped,
    retryPending: entry.retryTimer !== undefined,
    attempts: entry.attempts,
  });

  return {
    acquire(channel) {
      const existing = entries.get(channel);
      if (existing) {
        existing.refs += 1;
        return;
      }
      const isPrivate = isPrivateChannel(channel);
      const entry: Entry = {
        channel,
        refs: 1,
        isPrivate,
        account: isPrivate ? getPrivateChannelAccount(channel) : undefined,
        onWire: false,
        acked: false,
        blocked: false,
        stopped: false,
        attempts: 0,
        failedThisCycle: false,
      };
      entries.set(channel, entry);
      subscribe(entry);
    },

    release(channel) {
      const entry = entries.get(channel);
      if (!entry) return;
      entry.refs -= 1;
      if (entry.refs > 0) return;
      cancelRetry(entry);
      unsubscribe(entry);
      entries.delete(channel);
    },

    onConnect() {
      connected = true;
      // A new Socket.IO session starts unauthenticated, whatever the last one was.
      authenticatedAccount = undefined;
      entries.forEach(resetForSession);
      entries.forEach((entry) => {
        if (!entry.isPrivate) subscribe(entry);
      });
    },

    onDisconnect() {
      connected = false;
      authenticatedAccount = undefined;
      entries.forEach((entry) => {
        entry.onWire = false;
        entry.acked = false;
        cancelRetry(entry);
      });
    },

    onAuthenticated(account) {
      const accountKey = account.toLowerCase();
      authenticatedAccount = accountKey;
      Array.from(entries.values()).forEach((entry) => {
        if (!entry.isPrivate) return;
        if (entry.account === accountKey) {
          // A fresh identity: an ACCOUNT_MISMATCH from the old one no longer applies.
          entry.stopped = false;
          subscribe(entry);
          return;
        }
        cancelRetry(entry);
        unsubscribe(entry);
        entries.delete(entry.channel);
      });
    },

    onDeauthenticated() {
      entries.forEach((entry) => {
        if (!entry.isPrivate) return;
        cancelRetry(entry);
        unsubscribe(entry);
      });
      authenticatedAccount = undefined;
    },

    handleServerError(error) {
      if (!error || typeof error !== "object") return "ignored";
      const matched = match(error);
      if (matched === "ambiguous") return "ambiguous";
      if (!matched) return "ignored";
      const entry = matched;
      const code = error.code;

      if (code && NEVER_RETRY_CODES.has(code)) {
        // The server will never accept this request; retrying only burns its rate limit.
        cancelRetry(entry);
        entry.onWire = false;
        entry.acked = false;
        entry.blocked = true;
        return "blocked";
      }

      if (code === "ACCOUNT_MISMATCH" || code === "UNKNOWN_INSTRUMENT") {
        // No timed retry: a new connection (or, for a mismatch, a new
        // authentication) is the only thing that can change the answer.
        cancelRetry(entry);
        entry.onWire = false;
        entry.acked = false;
        entry.stopped = true;
        return "stopped";
      }

      if (code === "AUTH_REQUIRED") {
        // The server holds no identity for this socket; the next auth replays it.
        cancelRetry(entry);
        entry.onWire = false;
        entry.acked = false;
        return "awaiting-auth";
      }

      if (code === "RATE_LIMITED") {
        entry.onWire = false;
        entry.acked = false;
        const base = clampedBackoffMs(entry.attempts, error.retryAfterMs);
        const jittered = base + Math.floor(base * RATE_LIMIT_JITTER_RATIO * random());
        entry.attempts += 1;
        scheduleRetry(entry, Math.min(RETRY_MAX_DELAY_MS, jittered), false);
        return "retry-scheduled";
      }

      if (code === "SUBSCRIPTION_LIMIT") {
        entry.onWire = false;
        entry.acked = false;
        if (entry.attempts >= SUBSCRIPTION_LIMIT_MAX_ATTEMPTS) {
          cancelRetry(entry);
          entry.stopped = true;
          report("subscription limit: giving up until reconnect", {
            channel: entry.channel,
            limit: error.limit,
          });
          return "stopped";
        }
        const delay = clampedBackoffMs(entry.attempts, error.retryAfterMs);
        entry.attempts += 1;
        scheduleRetry(entry, delay, false);
        return "retry-scheduled";
      }

      if (code === "SNAPSHOT_FAILED" || code === "INTERNAL") {
        // The server keeps the join (live updates flow) and follows with
        // `subscribed`; fill the gap from REST now and ask for the snapshot again.
        runRevalidators(entry.channel);
        entry.failedThisCycle = true;
        if (entry.attempts >= TRANSIENT_MAX_ATTEMPTS) {
          cancelRetry(entry);
          report("snapshot retries exhausted until reconnect", {
            channel: entry.channel,
            code,
          });
          return "stopped";
        }
        const delay = clampedBackoffMs(entry.attempts);
        entry.attempts += 1;
        scheduleRetry(entry, delay, true);
        return "retry-scheduled";
      }

      return "ignored";
    },

    handleServerAck(ack) {
      if (!ack || typeof ack !== "object") return false;
      // Private and candle acks name the room in fields: `{ channel: "orders", account }`.
      let fromFields: string | undefined;
      if (typeof ack.channel === "string" && !ack.channel.includes(":")) {
        const parts = [ack.account, ack.market, ack.resolution].filter(
          (part) => typeof part === "string" && part !== ""
        );
        if (parts.length) fromFields = [ack.channel, ...parts].join(":");
      }
      const matched = match(ack, fromFields);
      if (!matched || matched === "ambiguous" || !matched.onWire) return false;
      matched.acked = true;
      if (!matched.failedThisCycle) matched.attempts = 0;
      return true;
    },

    registerRevalidate(channel, fn) {
      const fns = revalidators.get(channel) || new Set();
      fns.add(fn);
      revalidators.set(channel, fns);
      return () => {
        const current = revalidators.get(channel);
        if (!current) return;
        current.delete(fn);
        if (!current.size) revalidators.delete(channel);
      };
    },

    revalidate(serverChannel) {
      if (revalidators.has(serverChannel)) return runRevalidators(serverChannel);
      const wanted = canonicalChannel(serverChannel);
      const key = Array.from(revalidators.keys()).find(
        (channel) => canonicalChannel(channel) === wanted
      );
      return key ? runRevalidators(key) : false;
    },

    isConnected: () => connected,
    getAuthenticatedAccount: () => authenticatedAccount,
    getEntry(channel) {
      const entry = entries.get(channel);
      return entry ? toState(entry) : undefined;
    },
    getEntries: () => Array.from(entries.values()).map(toState),

    dispose() {
      entries.forEach(cancelRetry);
      entries.clear();
      revalidators.clear();
    },
  };
};
